import axios from "../../../api/axios";

export const recipientKey = (email) => String(email || "").trim().toLowerCase();
export const isRecipientEmail = (email) => /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]{2,}$/.test(String(email || "").trim());
export const splitRecipientEmails = (text) => String(text || "").split(/[,;\s]+/).filter(Boolean);
export const isRecipientList = (text) => splitRecipientEmails(text).every(isRecipientEmail);

export function uniqueRecipients(rows) {
  const seen = new Set();
  return rows.filter((row) => {
    const key = recipientKey(row?.email);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function rowsFrom(response) {
  const data = response?.data?.data;
  if (Array.isArray(data)) return data;
  // Users without directory access may receive only their own user.
  if (data?.id) return [data];
  return [];
}

/** Existing authenticated, company-scoped directories; no cross-company cache. */
export async function loadRecipientOptions({ query, customerId, signal }) {
  const params = { query: query || undefined, page: 1, per_page: 30, sortBy: "name", sort: "asc" };
  const requests = [
    ["customer", customerId == null ? null : axios.get("/contacts/", { signal, params: { ...params, filters: [{ name: "CUSTOMER", value: customerId }] } })],
    ["team", axios.get("/users/", { signal, params: { ...params, active: true, showInDropdown: true } })],
    ["other", axios.get("/contacts/", { signal, params })],
  ];
  const results = await Promise.allSettled(requests.map(([, request]) => request));
  const items = [];
  const failed = [];
  let hasMore = false;
  results.forEach((result, index) => {
    const [group, request] = requests[index];
    if (!request) return;
    if (result.status === "rejected") { failed.push(group); return; }
    const rows = rowsFrom(result.value);
    hasMore ||= rows.length >= 30;
    rows.forEach((row) => {
      const rowCustomerId = row.customer_id ?? row.customer?.id;
      const resolvedGroup = group === "other" && customerId != null && String(rowCustomerId) === String(customerId) ? "customer" : group;
      const name = row.displayTitle || row.name || row.email;
      if (isRecipientEmail(row.email)) {
        items.push({
          key: `${group}:${row.id}`,
          email: row.email.trim(),
          name,
          mobile: row.mobile_work || row.mobile,
          group: resolvedGroup,
          channel: "work_email",
        });
      }
      // Extra selectable channel — never merges with work email; opt-in in the picker.
      if (group === "team" && isRecipientEmail(row.private_email)) {
        items.push({
          key: `${group}:${row.id}:private_email`,
          email: row.private_email.trim(),
          name,
          mobile: row.mobile,
          group: "team",
          channel: "private_email",
        });
      }
    });
  });
  return { items: uniqueRecipients(items), failed, hasMore };
}
